import React, { useState } from 'react';
import { Box, TextField, Button, Paper, Typography, Alert, Link, Grid, Chip, Stack } from '@mui/material';
import { School, ArrowForward, CheckCircleOutline, VerifiedUser } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useSchool } from '../contexts/SchoolContext';

const SchoolLogin: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { login } = useSchool();

  const handleSubmit = async () => {
    setError(null);
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #f8fafc 0%, #e0f2fe 50%, #eef2ff 100%)', px: 2, py: 6 }}>
      <Grid container spacing={4} sx={{ maxWidth: 1100, width: '100%', alignItems: 'center' }}>
        <Grid item xs={12} md={6}>
          <Box sx={{ p: { xs: 2, md: 4 } }}>
            <Chip icon={<VerifiedUser />} label="School Portal Access" color="primary" sx={{ mb: 2, fontWeight: 600 }} />
            <Typography variant="h3" sx={{ fontWeight: 800, mb: 2, color: '#0f172a' }}>
              Welcome back to your school management hub
            </Typography>
            <Typography variant="body1" sx={{ color: '#475569', lineHeight: 1.8, mb: 3 }}>
              Manage admissions, classes, assessment records, parent communication, and school performance from one secure dashboard.
            </Typography>
            <Stack spacing={2}>
              {[
                'Monitor students, staff, and performance across terms',
                'Track academic results, payments, and tickets in real time',
                'Manage class activities with a secure, centralized workflow'
              ].map((item) => (
                <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CheckCircleOutline color="success" />
                  <Typography color="text.secondary">{item}</Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={6} sx={{ p: 4, borderRadius: 4, maxWidth: 460, mx: 'auto', border: '1px solid rgba(148,163,184,0.22)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
              <Box sx={{ p: 1.5, borderRadius: '50%', background: 'linear-gradient(135deg, #0ea5e9, #2563eb)', color: '#fff', display: 'flex' }}>
                <School />
              </Box>
            </Box>
            <Typography variant="h4" sx={{ mb: 1, textAlign: 'center', fontWeight: 700, color: '#0f172a' }}>
              School Login
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 3 }}>
              Sign in to continue to your school administration dashboard.
            </Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <TextField
              label="School email"
              type="email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Password"
              type="password"
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ mb: 3 }}
            />
            <Button
              variant="contained"
              fullWidth
              onClick={handleSubmit}
              disabled={loading}
              endIcon={<ArrowForward />}
              sx={{ mb: 2, py: 1.5, fontSize: '1rem', fontWeight: 700, borderRadius: 2 }}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
            <Box sx={{ textAlign: 'center' }}>
              <Link component={RouterLink} to="/school/signup" sx={{ textDecoration: 'none', fontWeight: 600 }}>
                Don&apos;t have an account? Register your school
              </Link>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 3 }}>
              All rights reserved. © {new Date().getFullYear()} EduTech Hub.
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SchoolLogin;